import {
  queryOptions,
  useMutation,
  type UseMutationResult,
} from "@tanstack/react-query"
import { request, type ResponseError } from "@utils/request"

import type { SolutionSettings } from "./solutionSettings"

type Solutions = {
  ciSolutionUid: string
  name: string
}

export type SearchEngines = {
  id: number
  name: string
  description: string | null
}

type SearchEngineListInput = {
  url: string
  username: string
  password: string
}

type SearchEngineListResponse = {
  searchEngines: SearchEngines[]
}

type SearchEngineListQueryKey = readonly [
  "searchEngineList",
  string,
  string,
  string,
]

const SEARCH_ENGINE_CONFLICT_STATUS = 409

type SearchEngineListQueryOptions = ReturnType<
  typeof queryOptions<
    SearchEngineListResponse,
    ResponseError,
    SearchEngineListResponse,
    SearchEngineListQueryKey
  >
>

type TestSearchEngineConnectionInput = {
  url: string
  username: string
  password: string
  searchEngineId: number
}

type TestSearchEngineConnectionResponse = {
  result: string
}

export const solutionSettingsQuery = queryOptions({
  queryKey: ["solutionSettings"] as const,
  queryFn: () => request.get<SolutionSettings>("/api/solution/v1"),
})

export const solutionsListQuery = queryOptions({
  queryKey: ["solutionsList"] as const,
  queryFn: () =>
    request.get<{ ciSolutions: Solutions[] }>("/api/solution/v1/ci-solutions"),
})

export const useUpdateSolutionSettings = (): UseMutationResult<
  Partial<SolutionSettings>,
  Error,
  Partial<SolutionSettings>
> => {
  return useMutation({
    mutationFn: (data: Partial<SolutionSettings>) => {
      return request.patch<Partial<SolutionSettings>>("/api/solution/v1", {
        json: data,
      })
    },
    onSuccess: async (_data, _variables, _result, { client }) => {
      await client.invalidateQueries(solutionSettingsQuery)
    },
  })
}

export const searchEngineListQuery = ({
  url,
  username,
  password,
}: SearchEngineListInput): SearchEngineListQueryOptions =>
  queryOptions({
    queryKey: ["searchEngineList", url, username, password] as const,
    queryFn: () =>
      request.post<SearchEngineListResponse>(
        "/api/solution/v1/search-engines",
        {
          json: { url, username, password },
        },
      ),
    enabled: Boolean(url && username && password),
    retry: (failureCount, error) => {
      if (error.statusCode === SEARCH_ENGINE_CONFLICT_STATUS) {
        return false
      }
      return error.statusCode === 0 && failureCount < 2
    },
  })

export const useTestSearchEngineConnection = (): UseMutationResult<
  TestSearchEngineConnectionResponse,
  Error,
  TestSearchEngineConnectionInput
> => {
  return useMutation({
    mutationFn: ({
      url,
      username,
      password,
      searchEngineId,
    }: TestSearchEngineConnectionInput) =>
      request.post<TestSearchEngineConnectionResponse>(
        "/api/solution/v1/validate-search-engine",
        {
          json: {
            url,
            username,
            password,
            searchEngineId,
          },
        },
      ),
  })
}
